import React, { useEffect, useState } from 'react';
import Card from '../../components/Card';
import { useAuth } from '../../context/AuthContext';
import { useT } from '../../utils/useTranslation';
import { db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';

export default function MyWritings() {
    const { user } = useAuth();
    const t = useT();
    const isRTL = t.language === 'sindhi';
    
    const [writings, setWritings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedWriting, setSelectedWriting] = useState(null);

    useEffect(() => {
        const loadData = async () => {
            if (!user) return;
            setLoading(true);
            try {
                // Fetch all events to extract contributions
                const eventsSnap = await getDocs(collection(db, 'events'));
                const allEvents = eventsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                
                const myWritings = [];

                allEvents.forEach(ev => {
                    if (ev.contributions) {
                        ev.contributions.forEach(c => {
                            if (c.memberId === user.id || c.memberId === user.memberId) {
                                myWritings.push({ 
                                    ...c, 
                                    eventTitle: isRTL ? (ev.titleSindhi || ev.title) : ev.title, 
                                    eventId: ev.id 
                                });
                            }
                        });
                    }
                });

                setWritings(myWritings.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
            } catch (err) {
                console.error("Error loading writings:", err);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [user, isRTL]);

    return (
        <div className="fade-in" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
            <div style={{ marginBottom: 32 }}>
                <h1 style={{ 
                    fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', 
                    fontSize: 32, 
                    fontWeight: 800, 
                    color: 'var(--sindhi-navy)',
                    marginBottom: 8
                }}>
                    {isRTL ? 'منهنجون لکڻيون' : 'My Writings'}
                </h1>
                <p style={{ color: 'var(--sindhi-text-muted)', fontSize: 16 }}>
                    {isRTL ? 'توهان جون اهي تمام لکڻيون جيڪي توهان مختلف گڏجاڻين ۾ پيش ڪيون آهن.' : 'A collection of all your writings submitted during various events and meetings.'}
                </p>
            </div>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><div className="spinner" /></div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: 24 }}>
                    {writings.length > 0 ? writings.map((w, idx) => (
                        <motion.div 
                            key={idx} 
                            initial={{ opacity: 0, y: 20 }} 
                            animate={{ opacity: 1, y: 0 }} 
                            transition={{ delay: idx * 0.05 }}
                            whileHover={{ y: -5 }}
                            onClick={() => setSelectedWriting(w)}
                            style={{ cursor: 'pointer' }}
                        >
                            <Card style={{ padding: 32, height: '100%', display: 'flex', flexDirection: 'column', background: 'white', border: '1px solid var(--sindhi-cream)', borderRadius: 24, transition: 'box-shadow 0.3s ease' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                                    <span style={{ 
                                        fontSize: 10, 
                                        fontWeight: 800, 
                                        color: 'var(--sindhi-maroon)', 
                                        textTransform: 'uppercase',
                                        background: 'rgba(153, 27, 27, 0.05)',
                                        padding: '4px 10px',
                                        borderRadius: 6
                                    }}>
                                        {w.type}
                                    </span>
                                    <span style={{ fontSize: 11, color: 'var(--sindhi-text-muted)', fontWeight: 600 }}>
                                        {new Date(w.createdAt).toLocaleDateString(isRTL ? 'sd-PK' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                                    </span>
                                </div>
                                
                                <h3 style={{ 
                                    fontSize: 18, 
                                    fontWeight: 800, 
                                    color: 'var(--sindhi-navy)', 
                                    marginBottom: 12, 
                                    fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' 
                                }}>
                                    {w.eventTitle}
                                </h3>
                                
                                <p style={{ 
                                    fontSize: 15, 
                                    color: 'var(--sindhi-text)', 
                                    lineHeight: 1.7, 
                                    margin: 0, 
                                    opacity: 0.9, 
                                    fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit',
                                    flex: 1,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 4,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden'
                                }}>
                                    {w.content}
                                </p>
                                
                                <div style={{ 
                                    marginTop: 24, 
                                    paddingTop: 16, 
                                    borderTop: '1px dashed var(--sindhi-cream)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    fontSize: 12, 
                                    color: 'var(--sindhi-gold)', 
                                    fontWeight: 700 
                                }}>
                                    <span style={{ fontSize: 16 }}>🔍</span>
                                    {isRTL ? 'تفصيل ڏسڻ لاءِ ڪلڪ ڪريو' : 'Click to view details'}
                                </div>
                            </Card>
                        </motion.div>
                    )) : (
                        <div style={{ 
                            gridColumn: '1 / -1', 
                            textAlign: 'center', 
                            padding: '100px 0', 
                            background: 'white', 
                            borderRadius: 32, 
                            border: '1px dashed var(--sindhi-cream)' 
                        }}>
                            <div style={{ fontSize: 48, marginBottom: 20 }}>✍️</div>
                            <h3 style={{ color: 'var(--sindhi-navy)', marginBottom: 8 }}>{isRTL ? 'ڪا به لکڻي نه ملي' : 'No Writings Found'}</h3>
                            <p style={{ color: 'var(--sindhi-text-muted)' }}>
                                {t('noWritingsYet')}
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Writing Detail Modal */}
            <AnimatePresence>
                {selectedWriting && (
                    <div style={{
                        position: 'fixed', inset: 0, zIndex: 1000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: 24, background: 'rgba(10, 25, 47, 0.4)', backdropFilter: 'blur(8px)'
                    }}>
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            style={{
                                width: '100%', maxWidth: 800, maxHeight: '90vh',
                                background: 'white', borderRadius: 32, overflow: 'hidden',
                                boxShadow: '0 40px 100px -20px rgba(0,0,0,0.3)',
                                display: 'flex', flexDirection: 'column'
                            }}
                        >
                            {/* Modal Header */}
                            <div style={{ 
                                padding: '32px 40px', background: 'var(--sindhi-navy)', color: 'white',
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                            }}>
                                <div>
                                    <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--sindhi-gold)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 8 }}>
                                        {selectedWriting.type}
                                    </div>
                                    <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)' }}>
                                        {selectedWriting.eventTitle}
                                    </h2>
                                </div>
                                <button 
                                    onClick={() => setSelectedWriting(null)}
                                    style={{ 
                                        width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,0.1)', 
                                        border: 'none', color: 'white', fontSize: 24, cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                                >
                                    ✕
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div style={{ padding: 40, overflowY: 'auto', flex: 1 }}>
                                <div style={{ 
                                    display: 'flex', gap: 24, marginBottom: 32, padding: 20, 
                                    background: 'var(--sindhi-ivory)', borderRadius: 20, border: '1px solid var(--sindhi-cream)'
                                }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', opacity: 0.5, marginBottom: 4 }}>
                                            {isRTL ? 'تاريخ' : 'Date Added'}
                                        </div>
                                        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--sindhi-navy)' }}>
                                            {new Date(selectedWriting.createdAt).toLocaleDateString(isRTL ? 'sd-PK' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    </div>
                                    <div style={{ width: 1, background: 'var(--sindhi-cream)' }} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', opacity: 0.5, marginBottom: 4 }}>
                                            {isRTL ? 'گڏجاڻي / تقريب' : 'Meeting / Event'}
                                        </div>
                                        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--sindhi-navy)' }}>
                                            {selectedWriting.eventTitle}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ 
                                    fontSize: 18, color: 'var(--sindhi-text)', lineHeight: 1.8, 
                                    fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit',
                                    whiteSpace: 'pre-wrap', position: 'relative', paddingLeft: isRTL ? 0 : 24, paddingRight: isRTL ? 24 : 0
                                }}>
                                    <div style={{ 
                                        position: 'absolute', top: 0, [isRTL ? 'right' : 'left']: 0, bottom: 0, 
                                        width: 4, background: 'var(--sindhi-gold)', borderRadius: 2, opacity: 0.3 
                                    }} />
                                    {selectedWriting.content}
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div style={{ padding: '24px 40px', borderTop: '1px solid var(--sindhi-cream)', display: 'flex', justifyContent: 'flex-end', background: 'var(--sindhi-ivory)' }}>
                                <button 
                                    onClick={() => setSelectedWriting(null)}
                                    style={{ 
                                        padding: '12px 32px', borderRadius: 12, background: 'var(--sindhi-navy)', 
                                        color: 'white', border: 'none', fontWeight: 700, cursor: 'pointer',
                                        fontSize: 14, transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(10,25,47,0.1)'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                                    onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                                >
                                    {isRTL ? 'بند ڪريو' : 'Close'}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
